import { useEffect, useRef } from 'react';
import { MapPin, Plus, Minus } from 'lucide-react';

// Church locations with exact coordinates
const CHURCHES = [
  {
    id: 'karlskrona',
    name: 'Vår Fru av Fatima kyrka',
    address: 'Södra Kungsgatan 1, 371 31 Karlskrona',
    lat: 56.1612,
    lng: 15.5869,
    mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Södra+Kungsgatan+1,+371+31+Karlskrona,+Sweden',
  },
  {
    id: 'karlshamn',
    name: 'S:t Bonifatius kyrka',
    address: 'Norra Fogdelyckegatan 26, 374 38 Karlshamn',
    lat: 56.1707,
    lng: 14.8616,
    mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Norra+Fogdelyckegatan+26,+374+38+Karlshamn,+Sweden',
  },
  {
    id: 'olofstrom',
    name: 'S:t Antonius kyrka',
    address: 'Bruksgatan 8, 293 38 Olofström',
    lat: 56.2794,
    lng: 14.5391,
    mapsUrl: 'https://www.google.com/maps/dir/?api=1&destination=Bruksgatan+8,+293+38+Olofström,+Sweden',
  },
];

const ChurchMap = ({ height = '500px' }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    // Load Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    // Load Leaflet JS
    const loadLeaflet = () => {
      return new Promise((resolve) => {
        if (window.L) {
          resolve(window.L);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.onload = () => resolve(window.L);
        document.head.appendChild(script);
      });
    };

    const initMap = async () => {
      const L = await loadLeaflet();
      
      if (!mapRef.current || mapInstanceRef.current) return;

      // Calculate center point of all churches
      const centerLat = CHURCHES.reduce((sum, c) => sum + c.lat, 0) / CHURCHES.length;
      const centerLng = CHURCHES.reduce((sum, c) => sum + c.lng, 0) / CHURCHES.length;

      // Initialize map
      const map = L.map(mapRef.current, {
        center: [centerLat, centerLng],
        zoom: 10,
        zoomControl: false, // We'll add custom controls
      });

      mapInstanceRef.current = map;

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      // Custom church icon
      const churchIcon = L.divIcon({
        html: `
          <div style="
            background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid white;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          ">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2v4"/>
              <path d="M9 6h6"/>
              <path d="M12 6v4"/>
              <path d="M4 22V10l8-4 8 4v12"/>
              <path d="M10 22v-6h4v6"/>
            </svg>
          </div>
        `,
        className: 'church-marker',
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -36],
      });

      // Add markers for each church
      CHURCHES.forEach((church) => {
        const marker = L.marker([church.lat, church.lng], { icon: churchIcon })
          .addTo(map)
          .bindPopup(`
            <div style="min-width: 200px; padding: 8px;">
              <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1f2937;">${church.name}</h3>
              <p style="margin: 0 0 12px 0; font-size: 13px; color: #6b7280;">${church.address}</p>
              <a 
                href="${church.mapsUrl}" 
                target="_blank" 
                rel="noopener noreferrer"
                style="
                  display: inline-block;
                  background: #d97706;
                  color: white;
                  padding: 6px 12px;
                  border-radius: 6px;
                  text-decoration: none;
                  font-size: 13px;
                  font-weight: 500;
                "
              >
                Vägbeskrivning →
              </a>
            </div>
          `, {
            maxWidth: 300,
          });
        markersRef.current.push(marker);
      });

      // Fit map to show all markers
      const bounds = L.latLngBounds(CHURCHES.map(c => [c.lat, c.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    };

    initMap();

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      markersRef.current = [];
    };
  }, []);

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.zoomOut();
    }
  };

  const handleFitAll = () => {
    if (mapInstanceRef.current && window.L) {
      const bounds = window.L.latLngBounds(CHURCHES.map(c => [c.lat, c.lng]));
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl">
      {/* Map container */}
      <div 
        ref={mapRef} 
        style={{ height, width: '100%' }}
        className="z-0"
      />
      
      {/* Custom zoom controls */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 bg-white rounded-lg shadow-xl card-hover flex items-center justify-center hover:bg-gray-50 transition-colors border border-gray-200"
          title="Zooma in"
          aria-label="Zooma in"
        >
          <Plus className="w-5 h-5 text-gray-700" />
        </button>
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 bg-white rounded-lg shadow-xl card-hover flex items-center justify-center hover:bg-gray-50 transition-colors border border-gray-200"
          title="Zooma ut"
          aria-label="Zooma ut"
        >
          <Minus className="w-5 h-5 text-gray-700" />
        </button>
        <button
          onClick={handleFitAll}
          className="w-10 h-10 bg-white rounded-lg shadow-xl card-hover flex items-center justify-center hover:bg-gray-50 transition-colors border border-gray-200 mt-2"
          title="Visa alla kyrkor"
          aria-label="Visa alla kyrkor"
        >
          <MapPin className="w-5 h-5 text-amber-600" />
        </button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl card-hover p-3 border border-gray-200">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <path d="M12 2v4"/>
              <path d="M9 6h6"/>
              <path d="M12 6v4"/>
              <path d="M4 22V10l8-4 8 4v12"/>
            </svg>
          </div>
          <span>Våra kyrkor</span>
        </div>
      </div>
    </div>
  );
};

export default ChurchMap;
